import React, { useState, useEffect } from 'react';
import { Search, Plus, Trash2, Calendar, Save } from 'lucide-react';
import api from '@/api/api';
const FeeSetting = () => {
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [student, setStudent] = useState(null);
  const [courseFee, setCourseFee] = useState(null);
  const [seatBooking, setSeatBooking] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    discount_amount: 0,
    payment_schedule_type: 'semester',
    num_installments: 4,
    auto_divide: true,
    installments: []
  });

  const [calculatedData, setCalculatedData] = useState({
    total_fee: 0,
    booking_amount: 0,
    admission_amount: 0,
    balance_amount: 0
  });

  useEffect(() => {
    fetchSemesters();
  }, []);

  const fetchSemesters = async () => {
    try {
      const response = await api.get('/master-settings/semesters');
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const searchStudent = async () => {
    if (!admissionNumber) {
      alert('Please enter admission number');
      return;
    }

    setLoading(true);
    try {
      // Get student - handle both array and paginated response
      const studentsRes = await api.get(`/students/?search=${admissionNumber}&limit=100`);
      const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : (studentsRes.data?.data || []);
      const foundStudent = studentsData.find(s => s.admission_number === admissionNumber);
      
      if (!foundStudent) {
        alert('Student not found');
        setStudent(null);
        return;
      }

      setStudent(foundStudent);

      // Get course fee - handle both array and paginated response
      const courseFeeRes = await api.get('/course-fees/course-fees');
      const courseFeeData = Array.isArray(courseFeeRes.data) ? courseFeeRes.data : (courseFeeRes.data?.data || []);
      const fee = courseFeeData.find(f => f.course_id === foundStudent.course_id);
      
      if (!fee) {
        alert('Course fee not set for this course. Please set it first.');
        return;
      }
      setCourseFee(fee);

      // Get seat booking - handle both array and paginated response
      const bookingRes = await api.get(`/seat-booking/?search=${admissionNumber}&limit=100`);
      const bookingData = Array.isArray(bookingRes.data) ? bookingRes.data : (bookingRes.data?.data || []);
      const booking = bookingData.find(b => b.admission_number === admissionNumber);
      setSeatBooking(booking);

      // Calculate amounts
      const totalFee = fee.total_fee;
      const bookingAmount = booking?.booking_amount || booking?.amount || 0;
      const admissionAmount = 0; // You may need to fetch this from admissions table
      const balanceAmount = totalFee - bookingAmount - admissionAmount - formData.discount_amount;

      setCalculatedData({
        total_fee: totalFee,
        booking_amount: bookingAmount,
        admission_amount: admissionAmount,
        balance_amount: balanceAmount
      });

      // Initialize installments
      if (formData.payment_schedule_type === 'semester') {
        initializeSemesterInstallments(balanceAmount);
      } else {
        initializeCustomInstallments(balanceAmount);
      }

    } catch (error) {
      console.error('Error searching student:', error);
      alert('Error loading student data');
    } finally {
      setLoading(false);
    }
  };

  const initializeSemesterInstallments = (balanceAmount) => {
    const semesterCount = semesters.length;
    const amountPerSemester = formData.auto_divide ? (balanceAmount / semesterCount) : 0;

    const installments = semesters.map((sem, idx) => ({
      installment_number: idx + 1,
      semester_id: sem.id,
      semester_name: sem.name,
      due_date: '',
      amount: amountPerSemester
    }));

    setFormData({ ...formData, installments });
  };

  const initializeCustomInstallments = (balanceAmount) => {
    const numInstallments = parseInt(formData.num_installments) || 1;
    const amountPerInstallment = formData.auto_divide ? (balanceAmount / numInstallments) : 0;

    const installments = [];
    for (let i = 0; i < numInstallments; i++) {
      installments.push({
        installment_number: i + 1,
        due_date: '',
        amount: amountPerInstallment
      });
    }

    setFormData({ ...formData, installments });
  };

  const handleDiscountChange = (discount) => {
    const newDiscount = parseFloat(discount) || 0;
    const balanceAmount = calculatedData.total_fee - calculatedData.booking_amount - calculatedData.admission_amount - newDiscount;
    
    setFormData({ ...formData, discount_amount: newDiscount });
    setCalculatedData({ ...calculatedData, balance_amount: balanceAmount });

    // Recalculate installments
    if (student) {
      if (formData.payment_schedule_type === 'semester') {
        initializeSemesterInstallments(balanceAmount);
      } else {
        initializeCustomInstallments(balanceAmount);
      }
    }
  };

  const handleScheduleTypeChange = (type) => {
    setFormData({ ...formData, payment_schedule_type: type });
    if (student) {
      if (type === 'semester') {
        initializeSemesterInstallments(calculatedData.balance_amount);
      } else {
        initializeCustomInstallments(calculatedData.balance_amount);
      }
    }
  };

  const handleInstallmentChange = (index, field, value) => {
    const newInstallments = [...formData.installments];
    newInstallments[index][field] = value;
    setFormData({ ...formData, installments: newInstallments });
  };

  const handleSubmit = async () => {
    if (!student) {
      alert('Please search and select a student first');
      return;
    }

    // Validate installments
    for (const inst of formData.installments) {
      if (!inst.due_date || !inst.amount) {
        alert('Please fill all installment dates and amounts');
        return;
      }
    }

    setLoading(true);
    try {
      await api.post('/course-fees/fee-setting', {
        student_id: student.id,
        discount_amount: formData.discount_amount,
        payment_schedule_type: formData.payment_schedule_type,
        installments: formData.installments
      });

      alert('Fee setting created successfully!');
      
      // Reset form
      setAdmissionNumber('');
      setStudent(null);
      setCourseFee(null);
      setSeatBooking(null);
      setFormData({
        discount_amount: 0,
        payment_schedule_type: 'semester',
        num_installments: 4,
        auto_divide: true,
        installments: []
      });
    } catch (error) {
      console.error('Error creating fee setting:', error);
      alert(error.response?.data?.detail || 'Failed to create fee setting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Fee Setting</h1>
        <p className="text-sm text-gray-500 mt-1">Set fee installments for students</p>
      </div>

      {/* Search Student */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Search Student</h2>
        <div className="flex gap-3">
          <input
            type="text"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Enter admission number"
            value={admissionNumber}
            onChange={(e) => setAdmissionNumber(e.target.value)}
          />
          <button
            onClick={searchStudent}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            <Search className="h-4 w-4" />
            Search
          </button>
        </div>
      </div>

      {student && courseFee && (
        <>
          {/* Student Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Student Details</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Student Name</p>
                <p className="font-medium text-gray-900">{student.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Course</p>
                <p className="font-medium text-gray-900">{courseFee.course_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Batch</p>
                <p className="font-medium text-gray-900">{student.batch || student.semester_id || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Fee</p>
                <p className="font-medium text-gray-900">₹{calculatedData.total_fee.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Booking Amount</p>
                <p className="font-medium text-gray-900">₹{calculatedData.booking_amount.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Admission Amount</p>
                <p className="font-medium text-gray-900">₹{calculatedData.admission_amount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          {/* Fee Configuration */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Fee Configuration</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Amount (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={formData.discount_amount}
                onChange={(e) => handleDiscountChange(e.target.value)}
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Balance Amount (to be paid in installments)</p>
              <p className="text-2xl font-bold text-blue-600">₹{calculatedData.balance_amount.toLocaleString('en-IN')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Schedule</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="schedule_type"
                    value="semester"
                    checked={formData.payment_schedule_type === 'semester'}
                    onChange={(e) => handleScheduleTypeChange(e.target.value)}
                  />
                  <span>Semester Wise</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="schedule_type"
                    value="custom"
                    checked={formData.payment_schedule_type === 'custom'}
                    onChange={(e) => handleScheduleTypeChange(e.target.value)}
                  />
                  <span>Custom Date</span>
                </label>
              </div>
            </div>

            {formData.payment_schedule_type === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Installments</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={formData.num_installments}
                  onChange={(e) => {
                    setFormData({ ...formData, num_installments: e.target.value });
                    initializeCustomInstallments(calculatedData.balance_amount);
                  }}
                />
              </div>
            )}

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.auto_divide}
                  onChange={(e) => {
                    setFormData({ ...formData, auto_divide: e.target.checked });
                    if (e.target.checked) {
                      if (formData.payment_schedule_type === 'semester') {
                        initializeSemesterInstallments(calculatedData.balance_amount);
                      } else {
                        initializeCustomInstallments(calculatedData.balance_amount);
                      }
                    }
                  }}
                />
                <span className="text-sm text-gray-700">Auto-divide amount equally</span>
              </label>
            </div>
          </div>

          {/* Installments */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Installments</h2>
            <div className="space-y-3">
              {formData.installments.map((inst, idx) => (
                <div key={idx} className="grid grid-cols-4 gap-3 items-center p-3 border rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {formData.payment_schedule_type === 'semester' 
                        ? inst.semester_name 
                        : `Installment ${inst.installment_number}`}
                    </p>
                  </div>
                  <div>
                    <input
                      type="date"
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      value={inst.due_date}
                      onChange={(e) => handleInstallmentChange(idx, 'due_date', e.target.value)}
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      value={inst.amount}
                      onChange={(e) => handleInstallmentChange(idx, 'amount', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="text-sm text-gray-500">
                    ₹{inst.amount.toLocaleString('en-IN')}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900">Total:</span>
                <span className="text-xl font-bold text-blue-600">
                  ₹{formData.installments.reduce((sum, inst) => sum + (inst.amount || 0), 0).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            <Save className="h-5 w-5" />
            {loading ? 'Creating...' : 'Create Installments'}
          </button>
        </>
      )}
    </div>
  );
};

export default FeeSetting;
